import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IDatasource, IGetRowsParams, ColDef as ColDefCommunity, ColGroupDef as ColGroupDefCommunity } from 'ag-grid-community';
import { ToastPackage, ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AnnotationService } from 'src/app/services/annotation/annotation.service';
import { DataService } from 'src/app/services/data/data.service';
import { GridLanguageService } from 'src/app/services/grid-language/grid-language.service';
import { SwalService } from 'src/app/services/swal/swal.service';
import { TableActionButtonComponent } from '../table-action-button/table-action-button.component';
import { GridApi, ColumnApi, ColDef, ValueGetterParams, ICellRendererParams, ColGroupDef } from '@ag-grid-enterprise/all-modules'
import { AgGridAngular } from 'ag-grid-angular';
import { NgxSpinnerService } from 'ngx-spinner';
import { LocationService } from 'src/app/services/location/location.service';
import { LS } from 'src/app/helpers/localStorage.enum';

@Component({
  selector: 'app-annotations-table',
  templateUrl: './annotations-table.component.html',
  styleUrls: ['./annotations-table.component.sass']
})
export class AnnotationsTableComponent implements OnInit, OnChanges, OnDestroy {

  @Input() locIds: string[]

  @ViewChild(AgGridAngular) agGridComponent: AgGridAngular

  @ViewChild('limitOpt', { read: ElementRef }) limitOpt: ElementRef;
  paginationSize = 50;
  isPage = true;
  autoGroupColumnDef
  currentVal : number = 50
  permissions: any;
  get totalData() {
    return this.rowData.length
  }
  cacheSize = 50;

  defaultColDefs = {
    width: 150,
    editable: false,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    resizable: true,
    flex: 2,
    rowSelection: 'single',
  };

  frameworkComponents = {
    actionCellRenderer: TableActionButtonComponent,
  };

  columnDefs: (ColDef)[]

  gridApi: GridApi
  gridColumnApi: ColumnApi;
  rowData;

  hovering$ = new BehaviorSubject(null)
  leaving$ = new BehaviorSubject(null)

  currLoc: {
    id?: string
  } = {}

  unsub$: Subject<any> = new Subject()

  constructor(
    private router: Router,
    private swalService: SwalService,
    private annotationService: AnnotationService,
    private locationService: LocationService,
    private spinner: NgxSpinnerService,
    private dataService: DataService,
    private toastr: ToastrService,
    private datePipe: DatePipe,
    private elem: ElementRef,
    private _t: TranslateService,
    public gridLangugage: GridLanguageService
  ) { }

  ngOnInit(): void {
    this.permissions = JSON.parse(localStorage.getItem(LS.currentUser)).permissions;
    this.annotationService.reloadTable$.pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe(
        async (res) => {
          this.rowData = []
          if (res && this.locIds?.length) {

            this.currLoc.id = this.locIds[0]

            const getAnnotations = async (locId: string) => {
              return new Promise(async (resolve, reject) => {
                try {
                  let paramsReq = [];
                  let countr = 0;

                  paramsReq.push({ key: `filtered[${countr}][key]`, val: 'location' });
                  paramsReq.push({ key: `filtered[${countr}][val]`, val: locId });

                  const res = await this.annotationService.findAll(paramsReq).toPromise()

                  if (!res?.data?.length) resolve([])

                  resolve(res.data)
                }
                catch (err) {
                  reject(err)
                }
              })
            }

            this.spinner.show()
            await this.locIds.reduce(async (p, id) => {
              await p

              const locAnnots = await getAnnotations(id)
              this.rowData = this.rowData || []
              this.rowData = this.rowData.concat(locAnnots)

              return p
            }, Promise.resolve())

            this.setupColumnDefs()
            this.agGridComponent.columnApi.resetColumnState()
            this.initHoverListener()

            this.spinner.hide()
          }
        }
      );
  }

  /** 
   * Initialize hover listener for row hovering
   * Had to use 'cell' event due to row hover event being absent: https://www.ag-grid.com/javascript-grid-row-node/#reference-rowNodeMethods
   */
  initHoverListener() {
    if (!this.agGridComponent) return

    this.agGridComponent.api.addEventListener('cellMouseOver', (evt) => {
      if (!evt.node) return
      this.hovering$.next(evt.node.id)
    })

    this.agGridComponent.api.addEventListener('cellMouseOut', (evt) => {
      if (!evt.node) return
      this.leaving$.next(evt.node.id)
    })
  }

  /**
   * Setups column definitions
   * Supports dynamically loading additional column if required
   */

  sorted(event) {
    this.cacheSize = this.currentVal;
    this.paginationSize = this.currentVal;
    return this.gridApi.paginationSetPageSize(this.currentVal);
  }
  setupColumnDefs() {
    /** Creates new column definition */

    // this.autoGroupColumnDef = {
    //   comparator: function (valueA, valueB, nodeA, nodeB, isInverted) {
    
    
    //     var res = valueA == valueB ? 0 : valueA > valueB ? 1 : -1;
    //     return res;
    //   },
    //   field: 'severity_type',
    //   sort: 'asc',
    // };

    this.columnDefs = [
      {
        headerName: '',
        headerCheckboxSelection: true,
        checkboxSelection: true,
        floatingFilter: false,
        maxWidth: 50,
        editable: false,
        suppressColumnsToolPanel: true,
        sortable: false,
        filter: false,
      },
      {
        headerName: this._t.instant('Annotation Name'),
        field: 'name',
        sortable: true,
        floatingFilterComponentParams: { suppressFilterButton: true },
        filter: 'agTextColumnFilter',
      },
      {
        headerName: this._t.instant('Description'),
        field: 'description',
        sortable: true,
        floatingFilterComponentParams: { suppressFilterButton: true },
        filter: 'agTextColumnFilter',
        valueGetter: (params: ValueGetterParams) => {
          const { data } = params
          if (data?.description)
            return this._t.instant(data?.description);
        },
      },
      {
        headerName: this._t.instant('Area Size (m²)'),
        field: 'area_size',
        sortable: true,
        // filter: 'agNumberColumnFilter',
        filter: false,
        floatingFilterComponentParams: { suppressFilterButton: true },
        valueGetter: (params: ValueGetterParams) => {
          const { data } = params
          return data ? +data?.area_size : data
        },
        cellRenderer: (params) => {
          const value = params.getValue();
          if (typeof value === 'number')
            return `${value.toFixed(3)} m²`;
        },
      },
      {
        headerName: this._t.instant('Component'),
        field: 'location_component.name',
        sortable: true,
        filter: 'agSetColumnFilter',
        valueGetter: (params: ValueGetterParams) => {
          const { data } = params
          if (!data) return
          const { location_component: value } = data
          if (value?.name)
            return this._t.instant(value.name)
        }
      },
      {
        headerName: this._t.instant('Defect type'),
        field: 'annotation_type.name',
        sortable: true,
        valueGetter: (params: ValueGetterParams) => {
          const { data } = params
          if (!data) return
          const { annotation_type: value } = data
          if (value?.name)
            return this._t.instant(value.name)
        },
        cellRenderer: (params: ICellRendererParams) => {
          const value = params.getValue();
          if (value)
            return `<span class="badge badge-pill badge-secondary">${value}</span>`;
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          cellRenderer: (params: ICellRendererParams) => {
            const { value } = params
            if (value.includes('Select All'))
              return value
            else
              return `<span class="badge badge-pill badge-secondary">${value}</span>`;
          }
        }
      },
      {
        headerName: this._t.instant('Severity'),
        field: 'severity_type',
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          cellRenderer: (params: ICellRendererParams) => {
            /** There's no 'data' in params here. There's only 'value' that comes from 'filterValueGetter' */
            const { value } = params;
            try {
              /** 'value' could be not parseable (ie: 'Select All') hence the try catch */
              const severity_type = JSON.parse(value)
              if (severity_type)
                return `<span class="badge badge-pill text-light" style="background-color: ${severity_type?.description};">${this._t.instant(severity_type?.name)}</span>`;
            }
            catch (err) {
              return value
            }
          },
        },
        filterValueGetter: (params: ValueGetterParams) => {
          const { data } = params
          if (data?.severity_type)
            return JSON.stringify(data.severity_type)
        },
        valueGetter: (params: ValueGetterParams) => {
          const { data } = params
          if (data)
            return this._t.instant(data?.severity_type?.name)
        },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (data?.severity_type)
            return `<span class="badge badge-pill text-light" style="background-color: ${data?.severity_type?.description};">${this._t.instant(data?.severity_type?.name)}</span>`;
        },
        comparator: function (valueA, valueB, nodeA, nodeB, isInverted) {
          const a = nodeA?.data?.severity_type?.name
          const b = nodeB?.data?.severity_type?.name
          var res = a == b ? 0 : a > b ? 1 : -1;
          return res;
        },
      },
      {
        headerName: this._t.instant('Created At'),
        field: 'created_at',
        sortable: true,
        filter: 'agDateColumnFilter',
        floatingFilterComponentParams: { suppressFilterButton: true },
        valueGetter: (params: ValueGetterParams) => {
          const { data } = params;
          if (!data) return;
          const d = new Date(data.created_at);
          return new Date(d.getFullYear(), d.getMonth(), d.getDate())
        },
        filterParams: {
          buttons: ['reset'],
        },
        cellRenderer: (params) => {
          const value = params.getValue();
          if (value) return this.datePipe.transform(value, 'yyyy / M / dd');
        },
      },
      {
        headerName: this._t.instant('Updated At'),
        field: 'updated_at',
        sortable: true,
        filter: 'agDateColumnFilter',
        floatingFilterComponentParams: { suppressFilterButton: true },
        valueGetter: (params: ValueGetterParams) => {
          const { data } = params;
          if (!data) return;
          const d = new Date(data.created_at);
          return new Date(d.getFullYear(), d.getMonth(), d.getDate())
        },
        filterParams: {
          buttons: ['reset'],
        },
        cellRenderer: (params) => {
          const value = params.getValue();
          if (value) return this.datePipe.transform(value, 'yyyy / M / dd');
        },
      },
      {
        field: this._t.instant('Action'),
        cellRenderer: 'actionCellRenderer',
        filter: false,
        floatingFilterComponentParams: { suppressFilterButton: true },
        cellRendererParams: {
          hovering$: this.hovering$,
          leaving$: this.leaving$,
          edit: this.permissions['update:any.annotation'] ? (data) => {
            this.router.navigate([
              '/projects',
              this.currLoc.id,
              'details',
              'annotations',
              'edit',
              data.id,
            ]);
          } : null,
          delete: this.permissions['delete:any.annotation'] ? (data) => {
            this.swalService.confirmDialog((swalRes) => {
              const msg = 
              this.annotationService
                .remove(data.id)
                .pipe(take(1))
                .subscribe((res) => {
                  const undoMsg = this._t.instant('Tap HERE to undo')
                  this.toastr
                    .info(undoMsg, '', {
                      progressBar: true,
                      timeOut: 7000,
                      enableHtml: true,
                    })
                    .onTap.pipe(take(1))
                    .subscribe(() => {
                      this.annotationService.restore(data.id).subscribe(res => this.annotationService.reloadTable$.next(true));
                    });
                  this.annotationService.reloadTable$.next(true);
                });
            })
          } : null,
        },
        minWidth: 150,
      },
    ]

    const isLocPopulated = !!this.rowData[0]?.location?.id
    if (isLocPopulated) {
      const colDef: ColDef = {
        headerName: this._t.instant('Facility/Asset'),
        field: 'location.name',
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
      }

      const firstCol = this.columnDefs.splice(0, 1)[0]
      this.columnDefs.unshift(firstCol, colDef)
    }

    this.agGridComponent.api.setColumnDefs(this.columnDefs as unknown as (ColDefCommunity | ColGroupDefCommunity)[])
  }

  onLimitChange(event) {
    const limit = event.target.value
    this.currentVal = limit;
    if (limit === 'all') {
      this.cacheSize = this.totalData;
      this.paginationSize = null;
      this.currentVal = this.totalData
      return this.gridApi.paginationSetPageSize(this.totalData);
    }
    this.cacheSize = +limit;
    this.paginationSize = +limit;
    this.gridApi.paginationSetPageSize(+limit);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.locIds?.currentValue) {
      // this.locIds = _locIds
      this.annotationService.reloadTable$.next(true)
    }
  }

  ngOnDestroy(): void {
    this.unsub$.next()
    this.unsub$.unsubscribe()
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    // if (this.currLoc?.id) params.api.setDatasource(this.dataSource);

    //Injects dropdown pagination
    const dropdownPage = document.querySelector('.ag-paging-panel');
    const find = setInterval(() => {
      if (dropdownPage) {
        dropdownPage.insertBefore(
          this.limitOpt.nativeElement,
          dropdownPage.firstChild
        );
        clearInterval(find)
      }
    }, 100);
  }

  /**
   * Runs when the grid first rendered the data
   * @param evt Event data
   */
  onFirstDataRendered(evt: any) {
    this.addDateMax()
  }

  /**
   * Modifies the dom element containing ag-grid input date field to add maximum date allowed
   */
  addDateMax() {
    const eles = this.elem.nativeElement.getElementsByClassName('ag-input-field-input ag-text-field-input')
    for (let i = 0; i < eles.length; i++) {
      const inputBox = eles.item(i)

      if (inputBox.type !== 'date') continue
      inputBox.max = '9999-12-31'
    }
  }

  downloadPdf() {
    /** Make request for downloading pdf */
    let initSelected = this.agGridComponent.api.getSelectedRows()
    let annotIds: string[] = []

    if (initSelected?.length) {
      /** forEachNodeAfterFilterAndSort is used to preserve the sorting done */
      this.agGridComponent.api.forEachNodeAfterFilterAndSort((node, idx) => {
        if (node.isSelected())
          annotIds.push(node.data?.id)
      })
    }
    /** When there's no selection, get all filtered rows (if not filtered, it'll get all rows) */
    else {
      /** forEachNodeAfterFilterAndSort is used to preserve the sorting done */
      this.agGridComponent.api.forEachNodeAfterFilterAndSort((node, idx) => {
        if (node.data?.id)
          annotIds.push(node.data?.id)
      })
    }

    this.swalService.confirmDialog(() => {
      if (annotIds?.length)
        this.locationService.generateReport(this.locIds[0], { annotationIds: annotIds }, true)
    }, {
      title: this._t.instant('Exporting PDF'),
      text: this._t.instant(`The report will contain {{no}} annotation(s). Continue?`, { no: annotIds.length }),
      confirmButtonText: this._t.instant('Ok'),
      customClass: {
        content: 'nowrap'
      }
    })

  }

  exportCsv() {
    const selected = this.agGridComponent.api.getSelectedRows()

    if (selected.length) return this.agGridComponent.api.exportDataAsCsv({
      onlySelected: true,
    })

    this.agGridComponent.api.exportDataAsCsv({
      columnKeys: this.columnDefs.filter(col => col.field).map(col => col.field)
    })
  }


}
